import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {
  type NativeId,
  convertElementIdToNativeId,
  getGuidNullable,
  getInstanceIndicesFromNativeId,
  generateInstanceNativeId,
  getInstanceSuffix,
  getGuid,
} from '@packages/systems/core/utils/NativeIdUtils';
import {Expressions, Component} from '@packages/systems/wfdl';
import {
  generateNativeIdFromElementInExpression,
  getAncestorProjectingElements,
  getBody,
  getCurrentComponentRender,
  getElementFromNodeInInstanceNativeId,
  getInnermostSymbolInstance,
  getAllComponentInstances,
  OPTIMIZE_CLICK_TRACKING_ATTR_NAME,
} from '@packages/utilities/platform';
import {getLiveComponentMap} from '@packages/utilities/platform-selectors';
import {WFID_ATTR, WF_DATA_ID_ATTR} from '@packages/utilities/html';
import {
  closest,
  getNativeIdFromDomElement,
  NORMAL_NODE,
} from '@packages/shared/utilities/DomUtils';
import {reportBugOnce} from '@packages/utilities/reporter';
import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {cacheMemo, singleMemo, weakMemo} from '@packages/utilities/memo';
import {OptimizeTrackingElementInfo} from '@packages/systems/optimization/types';
import {getActiveIframe} from '@packages/systems/core/helpers/SiteIframeMigrationHelper';

/**
 * Given a native DOMElement, retrieves the element that represents it
 */
const getExpressionElementForElement = (
  nativeElement: Element,
  designerState: DesignerStateType
): ExpressionElement | null => {
  if (!nativeElement || !nativeElement.getAttribute) {
    return null;
  }
  const elementId = getGuidNullable(
    nativeElement.getAttribute(WFID_ATTR) || null
  );
  if (elementId == null) {
    return null;
  }

  let element = null;
  designerState.components.find((comp) => {
    const render = Component.getRender(comp);
    element = Expressions.getElementById(render, elementId);
    return Boolean(element);
  });
  return element;
};

/**
 * Determines the target native id of an event. Webflow treats the HTML and BODY elements as one.
 */
export const getNativeIdFromEventTarget = ({
  nativeTarget,
  designerState,
  focusedSymbolNativeIdPath,
  isOpenCanvasActive,
}: {
  nativeTarget: Element | null;
  designerState: DesignerStateType;
  focusedSymbolNativeIdPath: string[];
  isOpenCanvasActive: boolean;
}): NativeId | null | undefined => {
  if (!nativeTarget || !nativeTarget.nodeName) {
    return null;
  }

  const tagName = nativeTarget.nodeName.toLowerCase();

  if (tagName === 'html' || tagName === 'body') {
    if (isOpenCanvasActive) {
      // In Multi-Context Canvas (open canvas), the html/body elements represent
      // the void space outside of page frames. Returning null prevents annotations,
      // hover highlights, click selections, and other mouse-driven interactions from
      // targeting this void area.
      return null;
    }

    // The <html> element doesn’t have an Expression Element, so proxy to the <body>
    // The <body> element can’t ever be special (symbol, dynitem, span, etc), so just return early...
    const bodyElement = getBody(getLiveComponentMap(designerState));
    return bodyElement ? convertElementIdToNativeId(bodyElement.id) : undefined;
  }

  // Find the corresponding Expression Element model
  const element = getExpressionElementForElement(nativeTarget, designerState);
  // If there is no attached Element (e.g. for spans, strong, inside symbols, etc), walk up to find the parent
  // This is guaranteed to grab at least the <body>’s Element model
  if (!element) {
    const nativeTargetParent = closest(nativeTarget, (nativeParent) =>
      // @ts-expect-error - Auto-suppressed while annotating DomUtils
      Boolean(getExpressionElementForElement(nativeParent, designerState))
    );
    if (nativeTargetParent && nativeTargetParent.nodeType === NORMAL_NODE) {
      return getNativeIdFromDomElement(
        nativeTargetParent as Element,
        focusedSymbolNativeIdPath
      );
    } else {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBugOnce(
        'nativeTargetParentError',
        'No parent with a native id for the clicked node',
        {
          nativeTarget,
        },
        {skipConsole: process.env.NODE_ENV === 'production'}
      );
      return null;
    }
  }

  return getNativeIdFromDomElement(nativeTarget, focusedSymbolNativeIdPath);
};

export const getNodeNativeIdPath = (nativeElement: Element): NativeId[] => {
  const nodeInInstanceNativeIdPathString =
    nativeElement.getAttribute(WF_DATA_ID_ATTR);
  return nodeInInstanceNativeIdPathString
    ? JSON.parse(nodeInInstanceNativeIdPathString)
    : [];
};

// On canvas user can interact (hover/click/invoke context menu/double click) with the elements that are inside the Lottie element
// These elements are part of the Lottie's svg and don't have any webflow related attributes carried on them
// and can not become selectable/hoverable etc on their own. For this reason we'll look up for the closest node that has "data-wf-id" attribute
export const getClosestNodeNativeIdPath = (
  nativeElement: Element | null
): NativeId[] => {
  const closestNativeNode =
    nativeElement && nativeElement.closest(`[${WF_DATA_ID_ATTR}]`);
  if (!closestNativeNode) return [];

  return getNodeNativeIdPath(closestNativeNode);
};

export const getAllNativeIdPathsWithinNativeNode = (
  rootNativeNode: HTMLElement
): Array<NativeId[]> => {
  const allNodesWithNativeIdPathAttr = rootNativeNode.querySelectorAll(
    `[${WF_DATA_ID_ATTR}]`
  );
  // @ts-expect-error - TS2769 - No overload matches this call.
  return Array.from<HTMLElement>(allNodesWithNativeIdPathAttr).map(
    getClosestNodeNativeIdPath
  );
};

export function getNativeIdPathRelativeToSelectedNativeIdPath(
  nativeIdPath: NativeId[],
  selectedNativeIdPath: NativeId[],
  componentMap: Component.ComponentMap
): NativeId[] {
  if (selectedNativeIdPath.length === 0) {
    return nativeIdPath;
  }

  const divergenceIndex = findIndexWhereNativeIdPathsDiverge(
    nativeIdPath,
    selectedNativeIdPath
  );

  const updatedNativeIdPath: Array<NativeId> = [];
  let instanceIndicesAfterDivergenceIndex: Array<number> = [];

  for (let i = 0; i < nativeIdPath.length; i++) {
    const nodeNativeId = nativeIdPath[i];
    const nodeId = getGuidNullable(nodeNativeId);

    if (nodeId === null) {
      reportBugOnce(
        'CorruptNativeIdPathError',
        'getGuidNullable(nodeNativeId) returned null',
        {
          team: 'structure',
          ticket: 'https://webflow.atlassian.net/browse/PLAT-2048',
          pathInfo: {
            nodeNativeId,
            nativeIdPath,
            index: i,
          },
        }
      );
      return [];
    }

    const selectedNodeNativeId = selectedNativeIdPath[i];
    let instanceIndices;

    if (i < divergenceIndex) {
      /*
        Before the index of divergence, we can take the instance suffix from
        the item in selected node native id path and use it as the instance
        suffix for the item in the node native id path.
      */

      // the check for selectedNodeNativeId here is purely for typescript,
      // before the divergence index selectedNodeNativeId should always be
      // defined
      instanceIndices = selectedNodeNativeId
        ? getInstanceIndicesFromNativeId(selectedNodeNativeId)
        : [];
    } else if (i === divergenceIndex) {
      /*
        At the index where the native id paths diverge, we need to recalculate
        the instance suffix for the item in the node native id path so that it
        is relative to the item in the selected node native id path.
      */
      const prevNativeId =
        updatedNativeIdPath.length > 0
          ? updatedNativeIdPath[updatedNativeIdPath.length - 1]
          : null;
      const prevInstanceIndices = prevNativeId
        ? getInstanceIndicesFromNativeId(prevNativeId)
        : [];

      const selectedNodeId = getGuidNullable(selectedNodeNativeId);

      // the check for selectedNodeNativeId here is purely for typescript,
      // at the divergence index selectedNodeNativeId should always be defined
      const selectedNodeInstanceIndices = selectedNodeNativeId
        ? getInstanceIndicesFromNativeId(selectedNodeNativeId)
        : [];

      // getNativeIdRelativeToSelectedElement operates inside the boundaries of
      // a single component, so we need to normalize the instance suffix of
      // the selected node native id path item to remove instance indices
      // from outside the component boundary.
      const fakeSelectedNodeInstanceIndices = selectedNodeInstanceIndices.slice(
        prevInstanceIndices.length
      );
      const fakeSelectedNodeNativeId =
        selectedNodeId && fakeSelectedNodeInstanceIndices.length > 0
          ? generateInstanceNativeId(
              selectedNodeId,
              fakeSelectedNodeInstanceIndices
            )
          : selectedNodeId;

      const root = getCurrentComponentRender(
        componentMap,
        nativeIdPath.slice(0, divergenceIndex)
      );

      // calculate node native id relative to selected node native id within
      // the component boundary
      const relativeNodeNativeId = root
        ? getNativeIdRelativeToSelectedElement(root)(fakeSelectedNodeNativeId)(
            nodeId
          )
        : null;

      const relativeInstanceIndices = relativeNodeNativeId
        ? getInstanceIndicesFromNativeId(relativeNodeNativeId)
        : [];

      // combine relative instance indices from within the component boundary
      // to instance indices from outside the component boundary
      instanceIndices = prevInstanceIndices.concat(relativeInstanceIndices);

      // store relative instance indices calculated here to use in adjusting
      // instance indices in the native id path after the divergence index
      instanceIndicesAfterDivergenceIndex = instanceIndices;
    } else {
      /*
        After the index of divergence, we need ensure that the instance suffix
        of every item starts with the relative instance suffix calculated at
        the index of divergence.
      */
      const nodeInstanceIndices = nodeNativeId
        ? getInstanceIndicesFromNativeId(nodeNativeId)
        : [];

      instanceIndices = instanceIndicesAfterDivergenceIndex.concat(
        nodeInstanceIndices.slice(instanceIndicesAfterDivergenceIndex.length)
      );
    }

    let updatedNativeId: NativeId;

    if (instanceIndices && instanceIndices.length > 0) {
      updatedNativeId = generateInstanceNativeId(nodeId, instanceIndices);
    } else {
      updatedNativeId = convertElementIdToNativeId(nodeId);
    }

    updatedNativeIdPath.push(updatedNativeId);
  }

  return updatedNativeIdPath;
}

/*
  Find the index where two native ids paths diverge

  The native ids in both paths at this index should correspond to elements
  within a single component.
*/
function findIndexWhereNativeIdPathsDiverge(
  nativeIdPathA: NativeId[],
  nativeIdPathB: NativeId[]
): number {
  let divergenceIndex = 0;

  for (let i = 0; i < nativeIdPathA.length; i++) {
    if (!nativeIdPathB[i]) {
      // We went beyond the array boundary of nativeIdPathB, which means
      // the previous index must be the point of divergence.
      break;
    }

    divergenceIndex = i;

    if (
      getGuidNullable(nativeIdPathA[i]) !== getGuidNullable(nativeIdPathB[i])
    ) {
      break;
    }
  }

  return divergenceIndex;
}

// caching results for 50 node ids should be sufficient
const elementIdMemo = cacheMemo(50);

const getNativeIdRelativeToSelectedElement = weakMemo((root: Expression) =>
  singleMemo((selectedNodeNativeId: string | null) =>
    elementIdMemo((elementId: string): string => {
      const element = Expressions.getElementById(root, elementId);

      if (!element) {
        return elementId;
      }

      const selectedNodeInstanceSuffix =
        getInstanceSuffix(selectedNodeNativeId);
      if (!selectedNodeNativeId || !selectedNodeInstanceSuffix) {
        // no node is selected or it doesn't have a suffix, so take the best guess
        return generateNativeIdFromElementInExpression(element.id, root);
      }

      const selectedElement = Expressions.getElementById(
        root,
        getGuid(selectedNodeNativeId)
      );
      const elementProjectingAncestors = getAncestorProjectingElements(
        element.id,
        root
      );
      const selectedElementProjectingAncestors = selectedElement
        ? getAncestorProjectingElements(selectedElement.id, root)
        : [];
      const nearestCommonProjectingAncestor = elementProjectingAncestors
        .reverse()
        .find((el) => selectedElementProjectingAncestors.includes(el));

      if (!nearestCommonProjectingAncestor) {
        // the two nodes are not in a common projecting tree, so take the best guess
        return generateNativeIdFromElementInExpression(element.id, root);
      }

      const commonProjectingAncestorIndex = elementProjectingAncestors.indexOf(
        nearestCommonProjectingAncestor
      );
      const instances = getInstanceIndicesFromNativeId(
        selectedNodeInstanceSuffix
      );

      // pare down the selected node's instances to the common ancestor
      const preciseInstancesNeeded =
        elementProjectingAncestors.length - commonProjectingAncestorIndex;

      const preciseInstances = instances.slice(0, preciseInstancesNeeded);

      // build up as many zero instances as needed to go from the common ancestor to the node
      const generalInstances = Array(commonProjectingAncestorIndex).fill('0');

      return generateInstanceNativeId(elementId, [
        ...preciseInstances,
        ...generalInstances,
      ]);
    })
  )
);

export const getAllOptimizeTrackingElementInfo = (
  componentMap: Component.ComponentMap
): OptimizeTrackingElementInfo[] => {
  return [
    ...(getActiveIframe()?.contentDocument?.querySelectorAll<HTMLElement>(
      `[${OPTIMIZE_CLICK_TRACKING_ATTR_NAME}]`
    ) ?? []),
  ].reduce<OptimizeTrackingElementInfo[]>((acc, nativeNode) => {
    const nativeIdPath = getClosestNodeNativeIdPath(nativeNode);
    const element = getElementFromNodeInInstanceNativeId(
      nativeIdPath,
      componentMap
    );
    if (element) {
      acc.push({
        element,
        nativeIdPath,
        nearestParentComponent:
          getInnermostSymbolInstance(nativeIdPath, componentMap) || undefined,
        componentInstanceChain: getAllComponentInstances(
          nativeIdPath,
          componentMap
        ),
        nativeNode,
      });
    }
    return acc;
  }, []);
};
